import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { Subscription, filter, map, switchMap, distinctUntilChanged, combineLatest, of } from 'rxjs'
import { LogService } from 'htv-lib'
import { CommonModule } from '@angular/common'
import { HTVButtonControllerDirective } from './app-button-controller.directive'
import { EventService } from './services/event.service'
import { LanguageService } from './services/language.service'
import { StateService } from './services/state.service'
import { LoaderComponent } from './pages/loader/loader.component'
import { ResolutionService } from './services/resolution.service'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [HTVButtonControllerDirective, CommonModule, RouterOutlet, LoaderComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  eventSubscription?: Subscription
  routerSubscription?: Subscription
  paramSubscription?: Subscription

  constructor(
    private activatedRoute: ActivatedRoute,
    private eventService: EventService,
    private logService: LogService,
    private languageService: LanguageService,
    private resolutionService: ResolutionService,
    private stateService: StateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search)
    const langCode = urlParams.get('language')
    const resolution = urlParams.get('resolution')

    this.languageService.init(langCode || undefined)
    this.resolutionService.init(resolution || undefined)

    this.handleNavigationAndParams()
  }

  ngAfterViewInit(): void {
    this.registerEventListener()
    this.registerKeyDownListener()
    this.registerPostMessageListener()
    this.checkIframeAndSendMessage()
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe()
    this.routerSubscription?.unsubscribe()
    this.paramSubscription?.unsubscribe()
  }

  handleNavigationAndParams() {
    const projectId$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => {
        let route = this.activatedRoute.firstChild
        while (route?.firstChild) {
          route = route.firstChild
        }
        return route?.params || of({})
      }),
      map(params => params['projectId'] as string | undefined),
      distinctUntilChanged()
    )

    const generatedProjectId$ = this.activatedRoute.queryParams.pipe(
      map(params => params['generatedProjectId'] as string | undefined),
      distinctUntilChanged()
    )

    this.routerSubscription = combineLatest([projectId$, generatedProjectId$]).subscribe(
      ([projectId, generatedProjectId]) => {
        if (projectId) {
          this.initializeProject(projectId, generatedProjectId)
        }
      }
    )

    this.paramSubscription = this.activatedRoute.queryParams.subscribe(queryParams => {
      const langCode = queryParams['language']
      if (langCode) {
        const currentLang = this.languageService.getSelectedLanguage()

        if (currentLang.code !== langCode) {
          this.languageService.setSelectedLanguageFromCode(langCode)
        }
      }
      const resolution = queryParams['resolution']
      if (resolution) {
        this.resolutionService.setResolution(resolution)
      }
    })
  }

  async initializeProject(projectId: string, generatedProjectId?: string): Promise<void> {
    await this.stateService.init(projectId, generatedProjectId)
  }

  registerEventListener(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe((key: string) => {
      switch (key) {
        default:
          break
      }
    })
  }

  registerKeyDownListener(): void {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      let key = event.key
      this.logService.log('Key: ' + key + ' ' + event.keyCode)
      if (event.keyCode >= 48 && event.keyCode <= 57) {
        key = '' + (event.keyCode - 48)
      } else if (event.keyCode >= 65 && event.keyCode <= 90) {
        key = event.key
      } else {
        switch (event.keyCode) {
          case 13:
            key = 'ENTER'
            break
          case 415:
            key = 'MEDIA-PLAY'
            break
          case 19:
            key = 'MEDIA-PAUSE'
            break
          case 413:
            key = 'MEDIA-STOP'
            break
          case 417:
            key = 'MEDIA-FASTFORWARD'
            break
          case 412:
            key = 'MEDIA-REWIND'
            break
          case 449:
            key = 'VOL-MUTE'
            break
          case 448:
            key = 'VOL-DOWN'
            break
          case 447:
            key = 'VOL-UP'
            break
          case 428:
            key = 'CHANNEL-DOWN'
            break
          case 427:
            key = 'CHANNEL-UP'
            break
          case 10221:
            key = 'SUBTITLE'
            break
          case 406:
            key = 'COL-BLUE'
            break
          case 405:
            key = 'COL-YELLOW'
            break
          case 404:
            key = 'COL-GREEN'
            break
          case 403:
            key = 'COL-RED'
            break
          case 37:
            key = 'ARROW-LEFT'
            break
          case 38:
            key = 'ARROW-UP'
            break
          case 39:
            key = 'ARROW-RIGHT'
            break
          case 40:
            key = 'ARROW-DOWN'
            break
          case 10009:
            key = 'BACK'
            break
          case 10182:
            key = 'EXIT'
            break
          case 10071:
          case 10072:
          case 10133:
          case 10135:
          case 10497:
            key = 'HOME'
            break
          case 457:
          case 458:
          case 10073:
            key = 'INFO'
            break
          case 10234:
            key = 'NETFLIX'
            break
          case 65376:
            key = 'DONE'
            break
          case 65385:
            key = 'CANCEL'
            break
          default:
            key = 'UNKNOWN : ' + event.keyCode
            break
        }
      }
      this.eventService.addEvent(key)
    })
  }

  checkIframeAndSendMessage(): void {
    this.logService.log(`is in iframe : ${this.isInIframe()}`)

    if (this.isInIframe()) {
      const message = {
        plugin: 'keyMap',
        params: {
          operation: 'addHotKey',
          Back: 'http://hes.genesis/tv/menu.do',
        },
      }
      const messageString = JSON.stringify(message)
      window.parent.postMessage(messageString, '*')
      this.logService.log(`Sent postMessage to parent: ${messageString}`)
    }
  }

  isInIframe(): boolean {
    return window.self !== window.top
  }

  registerPostMessageListener(): void {
    if (this.isInIframe()) {
      window.addEventListener('message', (event: MessageEvent) => {
        try {
          const keyCode = event.data
          if (!isNaN(keyCode)) {
            let mappedKeyCode = keyCode
            if (keyCode === 313) {
              mappedKeyCode = 13
            }

            this.logService.log('PostMessage Key: ' + mappedKeyCode)

            const isBackKey = mappedKeyCode === 10009
            const isHomeKey = [10071, 10072, 10133, 10135, 10497].includes(mappedKeyCode)

            if (isBackKey || isHomeKey) {
              console.log('Back or Home key pressed. Sending to parent window:')

              if (window.parent && window.parent !== window) {
                window.parent.location.href = 'http://hes.sensiq/tv/menu.do'
              }
            } else {
              const keydownEvent = new Event('keydown', {
                bubbles: true,
                cancelable: true,
              }) as any
              keydownEvent.keyCode = mappedKeyCode

              window.dispatchEvent(keydownEvent)
            }
          }
        } catch (error) {
          this.logService.error('Error handling postMessage: ' + error)
        }
      })
    }
  }
}
